import React, { useEffect, useRef, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { ExpenseCategory, TaxExpense, TaxExpenseInput } from '../../../types/database';
import { CATEGORY_OPTIONS, taxYearMonthOptions } from '../../../lib/expense-categories';
import { cn } from '../../../lib/utils';
import { calendarYearForMonth } from '../../../utils/taxUtils';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Select } from '../../ui/select';

interface ExpenseFormProps {
  taxYearStart: number;
  /** When set, the form edits this expense instead of adding a new one. */
  editing: TaxExpense | null;
  onSubmit: (expense: TaxExpenseInput) => Promise<void>;
  onCancelEdit: () => void;
  isSaving: boolean;
}

/** Current month if today falls inside the tax year, otherwise April. */
const defaultMonth = (taxYearStart: number): number => {
  const now = new Date();
  const month = now.getMonth() + 1;
  return calendarYearForMonth(taxYearStart, month) === now.getFullYear() ? month : 4;
};

export const ExpenseForm: React.FC<ExpenseFormProps> = ({ taxYearStart, editing, onSubmit, onCancelEdit, isSaving }) => {
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [month, setMonth] = useState(() => defaultMonth(taxYearStart));
  const [category, setCategory] = useState<ExpenseCategory>('other');
  const [error, setError] = useState<string | null>(null);

  // Load the expense being edited, or reset when switching tax year
  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setAmount(editing.amount.toString());
      setMonth(editing.month);
      setCategory(editing.category);
      setError(null);
      nameRef.current?.focus();
    }
  }, [editing]);

  useEffect(() => {
    setMonth(defaultMonth(taxYearStart));
  }, [taxYearStart]);

  const reset = () => {
    setName('');
    setAmount('');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    const value = parseFloat(amount);
    if (!trimmed) {
      setError('Give the expense a name');
      return;
    }
    if (isNaN(value) || value <= 0) {
      setError('Enter an amount above £0');
      return;
    }

    try {
      await onSubmit({ tax_year_start: taxYearStart, name: trimmed, amount: Math.round(value * 100) / 100, month, category });
      // Keep month + category so a run of similar expenses is quick to enter
      reset();
      nameRef.current?.focus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the expense');
    }
  };

  const handleCancel = () => {
    reset();
    onCancelEdit();
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'rounded-xl border p-3 transition-colors',
        editing ? 'border-brand/40 bg-brand/[0.05]' : 'border-line bg-raised/40'
      )}
    >
      {editing && <p className="mb-2 text-xs font-medium text-[#c9b3ff]">Editing “{editing.name}”</p>}
      <div className="grid grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-2 md:grid-cols-[minmax(0,1.6fr)_minmax(0,0.9fr)_minmax(0,1.1fr)_minmax(0,1.3fr)_auto]">
        <Input
          ref={nameRef}
          aria-label="Expense name"
          placeholder="What was it for?"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          containerClassName="col-span-2 md:col-span-1"
          className="col-span-2 md:col-span-1"
        />
        <Input
          aria-label="Amount"
          type="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          placeholder="0.00"
          leading="£"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="numeric"
        />
        <Select
          aria-label="Month"
          value={String(month)}
          onChange={(next) => setMonth(Number(next))}
          options={taxYearMonthOptions(taxYearStart)}
        />
        <Select
          aria-label="Category"
          value={category}
          onChange={(next) => setCategory(next as ExpenseCategory)}
          options={CATEGORY_OPTIONS}
          containerClassName="col-span-2 md:col-span-1"
        />
        <div className="col-span-2 flex gap-2 md:col-span-1">
          {editing && (
            <Button variant="secondary" size="icon" className="h-10 w-10" onClick={handleCancel} aria-label="Cancel editing">
              <X />
            </Button>
          )}
          <Button type="submit" disabled={isSaving} className="h-10 flex-1 md:flex-none">
            {editing ? <Check /> : <Plus />}
            {editing ? 'Save' : 'Add'}
          </Button>
        </div>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-xs text-danger">
          {error}
        </p>
      )}
    </form>
  );
};
